import { Suspense, useEffect } from 'react';
import { CacheProvider } from '@emotion/react';
import createCache from '@emotion/cache';
import styled from '@emotion/styled';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster, toast } from 'sonner';
import { showPushNotificationToast } from '@/utils/monitoringGroupColors';
import { GlobalAlarmBorder } from '@/components/alarm/GlobalAlarmBorder';
import { InstallAppBanner } from '@/components/common/InstallAppBanner';
import { ThemeProvider } from '@/theme';
import { GuideProvider } from '@/features/guide';
import ConfigurationLite from '@/pages/configuration/ConfigurationLite';
import MonitoringPage from '@/pages/monitoring/realtime/MonitoringPage';
import PlaybackPage from '@/pages/monitoring/playback/PlaybackPage';
import { WebSerialProvider } from '@/connections/serial/useSerial';
import { WebBleProvider } from '@/connections/ble/useBle';
import { DefaultLayout } from './components/layout/DefaultLayout';
import { GlobalLoadingProvider } from './components/providers/GlobalLoadingProvider';
import { GlobalModalProvider } from './components/providers/GlobalModalProvider';
import { ProtectedRoute } from './components/ProtectedRoute';
import { AuthProvider } from './components/providers/AuthProvider';
import { AuthRedirect } from './components/auth/AuthRedirect';
import { TimezoneProvider } from './contexts/TimezoneContext';
import { FCMProvider } from './components/providers/FCMProvider';
import { useNotificationStore } from './store/notificationStore';
import { ConfigurationMini } from '@/pages/configuration/ConfigurationMini';
import Register from '@/pages/auth/Register';
import Login from '@/pages/auth/Login';
import VerifyEmail from '@/pages/auth/VerifyEmail';
import ForgotPassword from '@/pages/auth/ForgotPassword';
import ResetPassword from '@/pages/auth/ResetPassword';
import FindEmail from '@/pages/auth/FindEmail';
import AllowedEmails from '@/pages/admin/AllowedEmails';
import AdminCompanyManagement from '@/pages/admin/AdminCompanyManagement';
import UserLogPage from '@/pages/admin/UserLog';
import SystemStatusPage from '@/pages/admin/SystemStatus';
import ManagementPage from '@/pages/management/ManagementPage';
import DashboardPage from '@/pages/dashboard/DashboardPage';
import DatalogPage from '@/pages/datalog/DatalogPage';
import { useSeoHead } from '@/hooks/useSeoHead';
import { supportsLocalUsbBleConfigurator } from '@/utils/browserCapabilities';

const cache = createCache({ key: 'css', prepend: true });
/** 폴링은 `@/api/query/polling` 및 대시보드·모니터링 latest 훅에서만 사용 */
const queryClient = new QueryClient();

/**
 * InstallAppBanner(문서 흐름 내 블록 요소)가 자기 높이만큼 밀어내고,
 * 아래 라우트 콘텐츠(DefaultLayout/로그인 페이지 등, 각각 height: 100%)가
 * 남은 뷰포트 공간을 차지하도록 하는 최상위 셸.
 */
const AppShell = styled.div`
  display: flex;
  flex-direction: column;
  height: 100vh;
  height: 100dvh;
`;

const RouteArea = styled.div`
  flex: 1;
  min-height: 0;
`;

interface RouteSeoMeta {
  title: string;
  description: string;
  robots?: string;
}

function resolveRouteSeo(pathname: string): RouteSeoMeta {
  const defaultMeta: RouteSeoMeta = {
    title: 'BELLCLOUD',
    description: 'BELLCLOUD Monitoring System',
    robots: 'noindex, nofollow',
  };

  const routeMetaList: Array<{ match: (path: string) => boolean; meta: RouteSeoMeta }> = [
    {
      match: path => path === '/login',
      meta: {
        title: 'BELLCLOUD | Login',
        description: 'BELLCLOUD login',
      },
    },
    {
      match: path => path === '/register',
      meta: {
        title: 'BELLCLOUD | Register',
        description: 'BELLCLOUD user registration',
      },
    },
    {
      match: path => path === '/verify-email',
      meta: {
        title: 'BELLCLOUD | Verify Email',
        description: 'BELLCLOUD email verification',
      },
    },
    {
      match: path => path === '/forgot-password',
      meta: {
        title: 'BELLCLOUD | Forgot PW',
        description: 'BELLCLOUD password recovery',
      },
    },
    {
      match: path => path === '/reset-password',
      meta: {
        title: 'BELLCLOUD | Reset PW',
        description: 'BELLCLOUD password reset',
      },
    },
    {
      match: path => path === '/find-email',
      meta: {
        title: 'Find Email | BELLCLOUD',
        description: 'BELLCLOUD account email finder',
      },
    },
    {
      match: path => path === '/',
      meta: {
        title: 'BELLCLOUD | Overview',
        description: 'BELLCLOUD dashboard',
      },
    },
    {
      match: path => path.startsWith('/monitoring/realtime'),
      meta: {
        title: 'BELLCLOUD | Realtime',
        description: 'Realtime gas monitoring dashboard',
      },
    },
    {
      match: path => path.startsWith('/monitoring/playback'),
      meta: {
        title: 'BELLCLOUD | Playback',
        description: 'Playback gas monitoring data',
      },
    },

    {
      match: path => path.startsWith('/configurator/lite'),
      meta: {
        title: 'BELLCLOUD | BELLLITE',
        description: 'BELLCLOUD Lite device configuration',
      },
    },
    {
      match: path => path.startsWith('/configurator/mini'),
      meta: {
        title: 'BELLCLOUD | BELLMINI',
        description: 'BELLCLOUD Mini device configuration',
      },
    },
    {
      match: path => path.startsWith('/admin/'),
      meta: {
        title: 'BELLCLOUD | Admin',
        description: 'BELLCLOUD admin page',
      },
    },
    {
      match: path => path.startsWith('/management'),
      meta: {
        title: 'BELLCLOUD | Management',
        description: 'BELLCLOUD site management',
      },
    },
    {
      match: path => path.startsWith('/detaLog'),
      meta: {
        title: 'BELLCLOUD | Log Data',
        description: 'BELLCLOUD datalog',
      },
    },
  ];

  const matched = routeMetaList.find(routeMeta => routeMeta.match(pathname));
  return matched?.meta ?? defaultMeta;
}

function SeoRouteMeta() {
  const location = useLocation();
  const routeSeo = resolveRouteSeo(location.pathname);
  useSeoHead({
    title: routeSeo.title,
    description: routeSeo.description,
    robots: routeSeo.robots,
  });
  return null;
}

function ConfiguratorIndexRedirect() {
  return supportsLocalUsbBleConfigurator() ? (
    <Navigate to="/configurator/lite" replace />
  ) : (
    <Navigate to="/" replace />
  );
}

function App() {
  const { addPushNotification } = useNotificationStore();

  // Service Worker에서 백그라운드 푸시 알림 수신 처리
  useEffect(() => {
    if ('serviceWorker' in navigator) {
      const handleSwMessage = (event: MessageEvent) => {
        if (event.data && event.data.type === 'PUSH_NOTIFICATION') {
          console.log('[SW→App] 백그라운드 푸시 수신:', event.data.payload);
          const { title, body, data, source } = event.data.payload;
          addPushNotification({ title, body, data, source: source ?? 'background' });
          // 전역 알람 테두리/경보음 루프가 즉시 동기화되도록 라이브 집계를 바로 갱신
          queryClient.invalidateQueries({ queryKey: ['dashboard'] });
          // toast 직접 호출
          const displayTitle = title || data?.i18n_title;
          const displayBody = body || data?.i18n_body;
          if (displayTitle) {
            showPushNotificationToast(
              { error: toast.error, warning: toast.warning, info: toast.info },
              String(displayTitle),
              displayBody ? String(displayBody) : undefined,
              (data || {}) as Record<string, unknown>,
              { duration: 8000 }
            );
          }
        }
      };
      navigator.serviceWorker.addEventListener('message', handleSwMessage);
      return () => {
        navigator.serviceWorker.removeEventListener('message', handleSwMessage);
      };
    }
  }, [addPushNotification]);

  return (
    <QueryClientProvider client={queryClient}>
      <CacheProvider value={cache}>
        <ThemeProvider defaultTheme="light">
          <GlobalLoadingProvider>
            <GlobalModalProvider>
              <TimezoneProvider>
                <AuthProvider>
                  <FCMProvider>
                    <WebSerialProvider>
                      <WebBleProvider>
                        <Toaster richColors position="top-right" />
                        <GlobalAlarmBorder />
                        <Suspense fallback={<div>Loading...</div>}>
                          <GuideProvider>
                            <Router>
                              <AppShell>
                                <InstallAppBanner />
                                <SeoRouteMeta />
                                <RouteArea>
                                  <Routes>
                                    {/* Auth routes - outside DefaultLayout */}
                                    <Route
                                      path="/register"
                                      element={
                                        <AuthRedirect>
                                          <Register />
                                        </AuthRedirect>
                                      }
                                    />
                                    <Route
                                      path="/login"
                                      element={
                                        <AuthRedirect>
                                          <Login />
                                        </AuthRedirect>
                                      }
                                    />
                                    <Route path="/verify-email" element={<VerifyEmail />} />
                                    <Route
                                      path="/forgot-password"
                                      element={
                                        <AuthRedirect>
                                          <ForgotPassword />
                                        </AuthRedirect>
                                      }
                                    />
                                    <Route
                                      path="/reset-password"
                                      element={
                                        <AuthRedirect>
                                          <ResetPassword />
                                        </AuthRedirect>
                                      }
                                    />
                                    <Route
                                      path="/find-email"
                                      element={
                                        <AuthRedirect>
                                          <FindEmail />
                                        </AuthRedirect>
                                      }
                                    />

                                    {/* Main app routes - inside DefaultLayout */}
                                    <Route path="/" element={<DefaultLayout />}>
                                      <Route
                                        index
                                        element={
                                          <ProtectedRoute>
                                            <DashboardPage />
                                          </ProtectedRoute>
                                        }
                                      />
                                      <Route
                                        path="monitoring"
                                        element={<Navigate to="/monitoring/realtime" replace />}
                                      />
                                      <Route
                                        path="monitoring/realtime"
                                        element={
                                          <ProtectedRoute requiredPermission="canAccessMonitoring">
                                            <MonitoringPage />
                                          </ProtectedRoute>
                                        }
                                      />
                                      <Route
                                        path="monitoring/playback"
                                        element={
                                          <ProtectedRoute requiredPermission="canAccessMonitoring">
                                            <PlaybackPage />
                                          </ProtectedRoute>
                                        }
                                      />
                                      <Route
                                        path="configurator"
                                        element={<ConfiguratorIndexRedirect />}
                                      />
                                      <Route
                                        path="configurator/lite"
                                        element={
                                          supportsLocalUsbBleConfigurator() ? (
                                            <ConfigurationLite />
                                          ) : (
                                            <Navigate to="/" replace />
                                          )
                                        }
                                      />
                                      <Route
                                        path="configurator/mini"
                                        element={
                                          supportsLocalUsbBleConfigurator() ? (
                                            <ConfigurationMini />
                                          ) : (
                                            <Navigate to="/" replace />
                                          )
                                        }
                                      />
                                      <Route
                                        path="admin/allowed-emails"
                                        element={
                                          <ProtectedRoute requiredPermission="canAccessAllowedEmails">
                                            <AllowedEmails />
                                          </ProtectedRoute>
                                        }
                                      />
                                      <Route
                                        path="admin/users"
                                        element={<Navigate to="/admin/companies" replace />}
                                      />
                                      <Route
                                        path="admin/admin-accounts"
                                        element={<Navigate to="/admin/companies" replace />}
                                      />
                                      <Route
                                        path="admin/companies"
                                        element={
                                          <ProtectedRoute requiredPermission="canAccessAdmin">
                                            <AdminCompanyManagement />
                                          </ProtectedRoute>
                                        }
                                      />
                                      <Route
                                        path="admin/user-log"
                                        element={
                                          <ProtectedRoute requiredPermission="canViewSystemLogs">
                                            <UserLogPage />
                                          </ProtectedRoute>
                                        }
                                      />
                                      <Route
                                        path="admin/system-status"
                                        element={
                                          <ProtectedRoute requiredPermission="canViewSystemLogs">
                                            <SystemStatusPage />
                                          </ProtectedRoute>
                                        }
                                      />
                                      <Route
                                        path="management"
                                        element={
                                          <ProtectedRoute requiredPermission="canAccessSites">
                                            <ManagementPage />
                                          </ProtectedRoute>
                                        }
                                      />
                                      <Route
                                        path="detaLog"
                                        element={
                                          <ProtectedRoute requiredPermission="canAccessSites">
                                            <DatalogPage />
                                          </ProtectedRoute>
                                        }
                                      />
                                    </Route>
                                    <Route path="*" element={<Navigate to="/login" replace />} />
                                  </Routes>
                                </RouteArea>
                              </AppShell>
                            </Router>
                          </GuideProvider>
                        </Suspense>
                      </WebBleProvider>
                    </WebSerialProvider>
                  </FCMProvider>
                </AuthProvider>
              </TimezoneProvider>
            </GlobalModalProvider>
          </GlobalLoadingProvider>
        </ThemeProvider>
      </CacheProvider>
    </QueryClientProvider>
  );
}

export default App;
